'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { PlayCircle, Hash, Shuffle } from 'lucide-react';

interface QuickActionsProps {
  currentWeek: number;
}

export default function QuickActions({ currentWeek }: QuickActionsProps) {
  const router = useRouter();
  const [jumpValue, setJumpValue] = useState('');
  const [showJump, setShowJump] = useState(false);

  const handleJump = () => {
    const n = parseInt(jumpValue, 10);
    if (n >= 1 && n <= 80) {
      router.push(`/week-detail-view?week=${n}`);
    }
  };

  const handleRandom = () => {
    const randomWeek = Math.floor(Math.random() * currentWeek) + 1;
    router.push(`/week-detail-view?week=${randomWeek}`);
  };

  return (
    <div
      style={{
        backgroundColor: 'var(--bg-card)',
        border: '1px solid var(--border)',
        borderRadius: '12px',
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
      }}
    >
      <h2 style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)', letterSpacing: '0.01em' }}>
        Quick Actions
      </h2>

      <button
        onClick={() => router.push(`/week-detail-view?week=${currentWeek}`)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '12px 14px',
          borderRadius: '8px',
          border: '1px solid var(--accent-border)',
          backgroundColor: 'var(--accent-glow)',
          color: 'var(--accent)',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: 500,
          transition: 'all 150ms ease',
          width: '100%',
          textAlign: 'left',
        }}
        onMouseEnter={e => {
          (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(29,158,117,0.2)';
        }}
        onMouseLeave={e => {
          (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--accent-glow)';
        }}
      >
        <PlayCircle size={18} />
        Continue Week {currentWeek}
      </button>

      <button
        onClick={() => setShowJump(v => !v)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '12px 14px',
          borderRadius: '8px',
          border: '1px solid var(--border)',
          backgroundColor: 'var(--bg-elevated)',
          color: 'var(--text-secondary)',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: 500,
          transition: 'all 150ms ease',
          width: '100%',
          textAlign: 'left',
        }}
        onMouseEnter={e => {
          (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--bg-card-hover)';
          (e.currentTarget as HTMLElement).style.color = 'var(--text-primary)';
        }}
        onMouseLeave={e => {
          (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--bg-elevated)';
          (e.currentTarget as HTMLElement).style.color = 'var(--text-secondary)';
        }}
      >
        <Hash size={18} />
        Jump to Week
      </button>

      {showJump && (
        <div style={{ display: 'flex', gap: '8px' }}>
          <input
            type="number"
            min={1}
            max={80}
            value={jumpValue}
            onChange={e => setJumpValue(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleJump()}
            placeholder="1–80"
            style={{
              flex: 1,
              padding: '8px 12px',
              borderRadius: '6px',
              border: '1px solid var(--border)',
              backgroundColor: 'var(--bg-input)',
              color: 'var(--text-primary)',
              fontSize: '14px',
              fontFamily: 'IBM Plex Mono, monospace',
              outline: 'none',
            }}
            autoFocus
          />
          <button
            onClick={handleJump}
            style={{
              padding: '8px 14px',
              borderRadius: '6px',
              border: '1px solid var(--accent-border)',
              backgroundColor: 'var(--accent)',
              color: '#fff',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 600,
              transition: 'all 150ms ease',
            }}
          >
            Go
          </button>
        </div>
      )}

      <button
        onClick={handleRandom}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '12px 14px',
          borderRadius: '8px',
          border: '1px solid var(--border)',
          backgroundColor: 'var(--bg-elevated)',
          color: 'var(--text-secondary)',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: 500,
          transition: 'all 150ms ease',
          width: '100%',
          textAlign: 'left',
        }}
        onMouseEnter={e => {
          (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--bg-card-hover)';
          (e.currentTarget as HTMLElement).style.color = 'var(--text-primary)';
        }}
        onMouseLeave={e => {
          (e.currentTarget as HTMLElement).style.backgroundColor = 'var(--bg-elevated)';
          (e.currentTarget as HTMLElement).style.color = 'var(--text-secondary)';
        }}
      >
        <Shuffle size={18} />
        Random Review
      </button>
    </div>
  );
}